import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import MessageBox from "reusableComponents/MessageBox";
import styles from "./styles.module.scss";

const AccountTypeDropdown = ({
  options,
  selectedOption,
  onSelect,
  placeholder = "Select account type",
  label = null,
  required = false,
  showRequiredText = false,
  error = null,
  disabled = false,
}) => {
  const [t] = useTranslation("global");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const dropdownMenuRef = useRef(null);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleOptionClick = (option) => {
    if (!disabled) {
      onSelect(option);
      setIsOpen(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Adjust dropdown position
  useEffect(() => {
    if (isOpen && dropdownMenuRef.current && dropdownRef.current) {
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const menuElement = dropdownMenuRef.current;
      
      // Reset positioning
      menuElement.style.removeProperty('bottom');
      menuElement.style.removeProperty('top');
      menuElement.style.removeProperty('width');
      menuElement.style.removeProperty('min-width');
      
      // Set width to match the button
      menuElement.style.minWidth = `${dropdownRect.width}px`;
      
      // Check if there's enough space below
      const spaceBelow = window.innerHeight - dropdownRect.bottom;
      const menuHeight = Math.min(menuElement.scrollHeight, 240);
      
      if (spaceBelow < menuHeight && dropdownRect.top > menuHeight) {
        // Open upwards if not enough space below
        menuElement.style.bottom = '100%';
        menuElement.style.top = 'auto';
        menuElement.style.marginBottom = '8px';
        menuElement.style.marginTop = '0';
      } else {
        // Open downwards
        menuElement.style.top = '100%';
        menuElement.style.bottom = 'auto';
        menuElement.style.marginTop = '8px';
        menuElement.style.marginBottom = '0';
      }
    }
  }, [isOpen]);

  return (
    <div className={`${styles.accountTypeDropdown} ${disabled ? styles.disabledWrapper : ""}`}>
      {label && (
        <p className={styles.dropdownLabel}>
          {label}
          {required ? (
            <span
              className={styles.requiredIndicator}
              style={{ fontSize: showRequiredText ? "inherit" : "18px" }}
            >
              {showRequiredText ? ` (${t("provide.step2.pictures.required")} *)` : `(*)`}
            </span>
          ) : (
            <span className={styles.requiredIndicator}></span>
          )}
        </p>
      )}
      
      <div className={styles.dropdownAndErrWrapper}>
        <div className={styles.dropdownContainer} ref={dropdownRef}>
          <button
            type="button"
            className={`${styles.dropdownButton} ${isOpen ? styles.open : ''} ${error ? styles.inputError : ''} ${disabled ? styles.disabledInput : ''}`}
            onClick={toggleDropdown}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            disabled={disabled}
          >
            <span className={styles.buttonText}>
              {selectedOption?.label || placeholder}
            </span>
            <span className={`${styles.arrow} ${isOpen ? styles.arrowUp : styles.arrowDown}`}>
              ▼
            </span>
          </button>

          {isOpen && (
            <ul 
              className={styles.dropdownMenu}
              role="listbox"
              ref={dropdownMenuRef}
            >
              {options.map((option) => (
                <li
                  key={option.value}
                  className={`${styles.dropdownItem} ${
                    selectedOption?.value === option.value ? styles.selected : ''
                  }`}
                  onClick={() => handleOptionClick(option)}
                  role="option"
                  aria-selected={selectedOption?.value === option.value}
                >
                  <span className={styles.optionText}>{option.label}</span>
                  {selectedOption?.value === option.value && (
                    <span className={styles.checkmark}>✓</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        {error && <MessageBox type="error" message={error} />}
      </div>
    </div>
  );
};

export default AccountTypeDropdown;